import type { UIMessage } from 'ai';
import { httpClient } from '@/services/http/httpClient';

export type ConversationSource =
  | 'general_chat'
  | 'content_generation'
  | 'content_refinement'
  | 'strategy_update'
  | 'onboarding_interview'
  | 'linguistic_analysis';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ConversationSummary {
  id: string;
  source: ConversationSource;
  status: string;
  messageCount: number;
  /** Backend returns truncated string (first ~100 chars).
   *  Note: HIV-101 spec says { role, content } but actual BE returns plain string. */
  lastMessage?: string;
  lastActivityAt?: string;
  updatedAt?: string;
}

export interface ConversationDetail {
  id: string;
  source: ConversationSource;
  status: string;
  messages: ChatMessage[];
  metadata: {
    agent: string;
    tokensUsed: number;
  };
}

interface ListConversationsResponse {
  conversations?: ConversationSummary[];
  data?: ConversationSummary[];
  total: number;
}

interface ListConversationsParams {
  page?: number;
  limit?: number;
  source?: ConversationSource;
  status?: 'active' | 'completed' | 'abandoned';
}

/** `POST /chat/attachments` — one stored file, attachable to the next message. */
export interface ChatAttachmentUpload {
  fileId: string;
  kind: 'image' | 'document';
  name: string;
  mimeType: string;
}

export interface ChatTurnOptions {
  conversationId?: string;
  context?: ConversationSource;
  relatedEntityId?: string;
  relatedEntityType?: 'post' | 'strategy';
  attachmentIds?: string[];
}

/** The `POST /chat` body. Empty fields are left out, not sent as null. */
export function buildChatTurnBody(
  message: string,
  options: ChatTurnOptions,
  fallbackConversationId: string | undefined
) {
  const conversationId = options.conversationId ?? fallbackConversationId;
  return {
    message,
    ...(conversationId ? { conversationId } : {}),
    ...(options.context ? { context: options.context } : {}),
    ...(options.relatedEntityId ? { relatedEntityId: options.relatedEntityId } : {}),
    ...(options.relatedEntityType ? { relatedEntityType: options.relatedEntityType } : {}),
    ...(options.attachmentIds?.length ? { attachmentIds: options.attachmentIds } : {}),
  };
}

/** What the chat transport sends for one turn. */
export function chatTurnRequest(
  apiBase: string,
  {
    messages,
    body,
    trigger,
  }: {
    messages: UIMessage[];
    body: Record<string, unknown> | undefined;
    trigger: 'submit-message' | 'regenerate-message';
  },
  fallbackConversationId: string | undefined
) {
  // Retry re-runs the stored turn (its attachments included); a plain
  // POST /chat would save the user's message a second time.
  if (trigger === 'regenerate-message' && fallbackConversationId) {
    return { api: `${apiBase}/chat/${fallbackConversationId}/regenerate`, body: {} };
  }
  const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
  const messageText =
    lastUserMsg?.parts
      ?.filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map(p => p.text)
      .join('') || '';
  return {
    body: buildChatTurnBody(messageText, (body ?? {}) as ChatTurnOptions, fallbackConversationId),
  };
}

export const chatApi = {
  uploadAttachment(file: File) {
    return httpClient.uploadFile<ChatAttachmentUpload>('/chat/attachments', file);
  },

  /** Removed from the composer before sending: stop it counting toward storage. */
  deleteAttachment(fileId: string) {
    return httpClient.delete<{ deleted: true }>(`/chat/attachments/${fileId}`);
  },

  async listConversations(params?: ListConversationsParams) {
    const query = new URLSearchParams(
      Object.entries(params || {})
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)])
    );
    return httpClient.get<ListConversationsResponse>(`/chat?${query}`);
  },

  async getConversation(conversationId: string) {
    return httpClient.get<ConversationDetail>(`/chat/${conversationId}`);
  },

  async archiveConversation(conversationId: string) {
    return httpClient.delete<{ archived: boolean }>(`/chat/${conversationId}`);
  },
};
